import { Identifier } from 'react-admin';
import {
  BumpResourceSheetInterface,
  BumpResourceSheetPaidBatchInterface,
  BumpResourceSheetType,
} from '@vatos-pas/common';

import {
  canEditHOServiceRepair,
  isJobPhaseBumpType,
  isPendingSupervisorStatus,
} from './utils';

/** POST target of the Bumpout Timesheet "Mark as Paid" action. */
export const BUMPOUT_MARK_AS_PAID_RESOURCE = 'bump-resource-sheet-paid-batch';

export const UNPRICED_BUMPS_WARNING =
  'Bumps have no price yet and count as $0.00 in these totals.';

/** Fields a selected row must carry for the bar to reason about it. */
export type BumpoutSelectionRow = Pick<
  BumpResourceSheetInterface,
  'id' | 'bumpResourceType' | 'repairType' | 'bumperApprovalStatus'
>;

export type BumpoutSelectionRowsById = {
  [id: string]: BumpoutSelectionRow | undefined;
};

/** Fields fetched beside `id` by Select all. */
export const SELECTION_ROW_FIELDS: (keyof BumpoutSelectionRow)[] = [
  'bumpResourceType',
  'repairType',
  'bumperApprovalStatus',
];

export type ApprovePermissions = {
  canApprove: boolean;
  canManageHOService: boolean;
};

/** A grid row carries an id but may lack any field the approvable rule reads. */
export type ApprovableRowFields = { id: Identifier } & Partial<
  Pick<
    BumpoutSelectionRow,
    'bumpResourceType' | 'repairType' | 'bumperApprovalStatus'
  >
>;

/**
 * Approvable rule for one row: an approver may approve a repair
 * payment row (never a job phase bump row) that is pending supervisor action,
 * HO Service rows only with the HO Service permission.
 */
export const isRowApprovableBy = (
  row: ApprovableRowFields,
  { canApprove, canManageHOService }: ApprovePermissions,
): boolean =>
  canApprove &&
  !isJobPhaseBumpType(row.bumpResourceType) &&
  canEditHOServiceRepair(row.repairType, canManageHOService) &&
  isPendingSupervisorStatus(row.bumperApprovalStatus);

const toSelectionRow = (row: BumpoutSelectionRow): BumpoutSelectionRow => ({
  id: row.id,
  bumpResourceType: row.bumpResourceType,
  repairType: row.repairType,
  bumperApprovalStatus: row.bumperApprovalStatus,
});

/** Adds `rows` to the map, replacing entries already present. */
export const mergeSelectionRows = (
  rowsById: BumpoutSelectionRowsById,
  rows: BumpoutSelectionRow[],
): BumpoutSelectionRowsById => {
  if (rows.length === 0) return rowsById;

  const merged: BumpoutSelectionRowsById = { ...rowsById };
  rows.forEach(row => {
    merged[String(row.id)] = toSelectionRow(row);
  });
  return merged;
};

/**
 * Builds the bump-resource-sheet-paid-batch body for the selection, split by
 * bumpResourceType; empty lists are omitted. Every selected id must be in
 * `rowsById`: the list clears its selection on mount, so a selected row was
 * always seen on a page or fetched by Select all.
 */
export const buildMarkAsPaidBody = (
  selectedIds: Identifier[],
  rowsById: BumpoutSelectionRowsById,
): BumpResourceSheetPaidBatchInterface => {
  const jobPhaseBumpIds: string[] = [];
  const repairPaymentIds: string[] = [];
  let unknownCount = 0;

  selectedIds.forEach(identifier => {
    const id = String(identifier);
    switch (rowsById[id]?.bumpResourceType) {
      case BumpResourceSheetType.JOB_BUMP_PHASE:
        jobPhaseBumpIds.push(id);
        break;
      case BumpResourceSheetType.REPAIR_PAYMENT:
        repairPaymentIds.push(id);
        break;
      default:
        unknownCount += 1;
    }
  });

  if (unknownCount > 0) {
    throw new Error(
      `Could not determine the row type of ${unknownCount} selected row(s).`,
    );
  }

  return {
    ...(jobPhaseBumpIds.length > 0 && { jobPhaseBumpIds }),
    ...(repairPaymentIds.length > 0 && { repairPaymentIds }),
  };
};

/** Bumps carry no price yet, so the summary counts them as $0.00. */
export const markAsPaidWarnings = (
  body: BumpResourceSheetPaidBatchInterface,
): string[] => (body.jobPhaseBumpIds?.length ? [UNPRICED_BUMPS_WARNING] : []);
